import { useEffect } from 'react'
import { isNil } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { isAfter } from 'date-fns'

import { loadUserProgressStart, setCurrentStep } from '../redux/modules/onboarding'
import { ONBOARDING_STEPS, userHidOnboarding } from '../components/onboarding/onboardingUtils'
import { selectProfile } from '../redux/modules/selectors'

const ROLL_OUT_DATE = new Date('2021-04-22')

export const useOnboarding = () => {
  const dispatch = useDispatch()
  const profile = useSelector(selectProfile)
  const userProgress = useSelector(state => state.onboarding.userProgress)
  const isLoadingUserProgress = useSelector(state => state.onboarding.isLoadingUserProgress)
  const errorLoadingUserProgress = useSelector(state => state.onboarding.errorLoadingUserProgress)
  const currentStep = useSelector(state => state.onboarding.currentStep)
  const skippedOnboarding = userHidOnboarding(profile?.userId)
  // Eligibility based on registration date
  const isEligible = isAfter(new Date(profile?.createdDate), ROLL_OUT_DATE) && !skippedOnboarding

  // Check if user completed any of the steps before
  const hasEmailTime = !isNil(userProgress?.dailyEmailSendTime)
  const hasCuisineTypes = !isNil(userProgress?.storeTypes)
  const hasMobileApp = userProgress?.hasMobileApp

  // Determine the default step
  let defaultStep = ONBOARDING_STEPS.COMPLETE
  if (!hasMobileApp && !skippedOnboarding) defaultStep = ONBOARDING_STEPS.MOBILE_DOWNLOAD
  if (!hasCuisineTypes) defaultStep = ONBOARDING_STEPS.CUISINE_TYPES
  if (!hasEmailTime) defaultStep = ONBOARDING_STEPS.EMAIL_SEND_TIME

  const hideOnboarding =
    defaultStep.value === ONBOARDING_STEPS.COMPLETE.value || // -> user has all the data needed that indicates completion
    currentStep?.value === ONBOARDING_STEPS.COMPLETE.value || // -> user has just completed the steps
    errorLoadingUserProgress || // -> an error occured so just hide it silently
    skippedOnboarding || // -> user skipped the onboarding
    !isEligible // -> user was created before this feature was rolled out

  useEffect(() => {
    if (isEligible) {
      dispatch(loadUserProgressStart())
    }
  }, [dispatch, isEligible])

  useEffect(() => {
    if (userProgress) {
      dispatch(setCurrentStep(defaultStep))
    }
  }, [dispatch, userProgress, defaultStep])

  return {
    defaultStep,
    hasEmailTime,
    hasCuisineTypes,
    hasMobileApp,
    isLoadingUserProgress,
    errorLoadingUserProgress,
    hideOnboarding,
    isEligible,
  }
}

export default useOnboarding
